import { type ReactNode, useState, useEffect } from 'react'
import { Search, BookOpen, CalendarDays } from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import { AppFooter } from '@/components/layout/AppFooter'
import { SettingsSheet } from '@/components/settings/SettingsSheet'
import { AccountChip } from '@/components/layout/AccountChip'
import { CommandPalette } from '@/components/CommandPalette'
import { cn } from '@/lib/utils'

export type AppMode = 'detail' | 'journal' | 'calendar'

interface AppShellProps {
  children: ReactNode
  mode: AppMode
  onModeChange: (mode: AppMode) => void
}

export function AppShell({ children, mode, onModeChange }: AppShellProps) {
  const [paletteOpen, setPaletteOpen] = useState(false)

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setPaletteOpen((o) => !o)
        return
      }
      // D/J/C view shortcuts — skip when typing
      const target = e.target as HTMLElement
      const isTyping =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      if (isTyping || e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key === 'd' || e.key === 'D') { e.preventDefault(); onModeChange('detail') }
      if (e.key === 'j' || e.key === 'J') { e.preventDefault(); onModeChange('journal') }
      if (e.key === 'c' || e.key === 'C') { e.preventDefault(); onModeChange('calendar') }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onModeChange])

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Skip link — visible only on keyboard focus */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:px-3 focus:py-1.5 focus:rounded focus:bg-background focus:text-foreground focus:ring-2 focus:ring-ring focus:text-sm focus:font-medium"
      >
        Skip to main content
      </a>
      <header className="sticky top-0 z-40 pt-[var(--sat)] bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <button
            type="button"
            onClick={() => onModeChange('detail')}
            className="font-serif text-[1.65rem] leading-none md:text-xl tracking-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            aria-label="Go to home — your life areas and priorities"
            aria-current={mode === 'detail' ? 'page' : undefined}
          >
            Plumb
          </button>
          <div className="flex items-center gap-2">
          {/* ⌘K trigger */}
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="hidden sm:flex items-center gap-2 h-8 px-2.5 rounded-md border border-border text-xs text-muted-foreground hover:text-foreground hover:border-ring transition-colors"
            aria-label="Open command palette"
          >
            <Search size={12} />
            <span>Search…</span>
          </button>
          <div className="flex items-center gap-0.5 text-xs">
            {([
              { m: 'journal' as AppMode,  label: 'Journal',  Icon: BookOpen     },
              { m: 'calendar' as AppMode, label: 'Calendar', Icon: CalendarDays },
            ]).map(({ m, label, Icon }) => (
              <button
                key={m}
                type="button"
                onClick={() => onModeChange(m)}
                className={cn(
                  'flex items-center justify-center p-2.5 md:px-3 md:py-1.5 rounded font-serif text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  mode === m
                    ? 'bg-secondary text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                aria-current={mode === m ? 'page' : undefined}
                aria-label={label}
              >
                <Icon size={24} className="md:hidden" aria-hidden />
                <span className="hidden md:inline">{label}</span>
              </button>
            ))}
          </div>
          <AccountChip />
          <SettingsSheet />
          </div>
        </div>
        <Separator />
      </header>

      <main
        id="main-content"
        className={cn(
          'flex-1 max-w-7xl w-full mx-auto px-4 md:px-6 py-6 md:py-8',
          (mode === 'journal' || mode === 'calendar') && 'flex flex-col',
        )}
      >
        {children}
      </main>

      {/* On mobile Detail the fixed life-area bar owns the bottom edge, so the
          footer only shows from md up there; Settings carries the version too. */}
      <AppFooter className={mode === 'detail' ? 'hidden md:block' : undefined} />

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onNavigate={onModeChange}
      />
    </div>
  )
}
